import { getLoraPreview, useLoras } from "@features/loras/use-loras";
import { Avatar } from "@radix-ui/themes";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

interface PromptInputProps {
  value?: string;
  modelId?: string;
  onChange?: (value: string, modelId?: string) => void;
  onBlur?: (value: string) => void;
}

export const PromptInput = ({
  value = "",
  onChange,
  onBlur,
}: PromptInputProps) => {
  const { t } = useTranslation();
  const { loras } = useLoras();
  const selectedLora = loras.data?.[0];
  const [isFocused, setIsFocused] = useState(false);
  const [content, setContent] = useState(value);
  const contentEditableRef = useRef<HTMLSpanElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Update internal state when value prop changes
  useEffect(() => {
    if (contentEditableRef.current) {
      // Only update if content is different to prevent cursor issues
      if (contentEditableRef.current.textContent !== value && !isFocused) {
        contentEditableRef.current.textContent = value;
        setContent(value);
      }
    }
  }, [value, isFocused]);

  // Auto focus on mount and select all text after animation
  useEffect(() => {
    // Set a timeout to make the contentEditable visible after 200ms
    const timer = setTimeout(() => {
      setIsVisible(true);
      if (contentEditableRef.current) {
        contentEditableRef.current.focus();

        // Select all text in the contentEditable
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(contentEditableRef.current);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, []);

  // Focus handler that also selects all text
  const focusAndSelectAll = () => {
    if (contentEditableRef.current) {
      contentEditableRef.current.focus();
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(contentEditableRef.current);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  };

  const handleInput = () => {
    // Directly get the text content rather than setting it
    const newContent = contentEditableRef.current?.textContent || "";
    setContent(newContent);
    onChange?.(newContent, selectedLora?.id);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
    onBlur?.(content);
  };

  return (
    <div
      className={`p-2 bg-white ${
        isFocused ? "bg-opacity-20" : "bg-opacity-10"
      } w-full`}
      style={{ borderRadius: "20px" }}
      onClick={(e) => {
        e.stopPropagation(); // Prevent click from reaching parent
        if (isVisible) {
          focusAndSelectAll();
        }
      }}
    >
      {!!selectedLora && (
        <motion.span
          className="mr-1 align-start"
          onClick={(e) => {
            e.stopPropagation(); // Prevent click from bubbling to parent div
            console.log("Lora clicked");
          }}
        >
          <Avatar
            size="1"
            src={getLoraPreview(selectedLora.id)}
            fallback="U"
            className="rounded-full align-middle -mt-1"
          />
          <span className="opacity-50 ml-1 align-start">
            {selectedLora.name?.split(" ")[0]}
          </span>
        </motion.span>
      )}
      <span
        ref={contentEditableRef}
        contentEditable
        className={twMerge(
          "outline-none focus:outline-none min-h-[1.2rem] align-start min-w-10",
          value ? "inline" : "inline-block"
        )}
        onInput={handleInput}
        onFocus={handleFocus}
        onBlur={handleBlur}
        suppressContentEditableWarning={true}
        data-placeholder={content ? "" : t("components.prompt_input.placeholder")}
        style={{
          minWidth: isVisible ? "4px" : "0px", // Start with zero width
          maxWidth: isVisible ? "none" : "0px", // Start with zero width
          opacity: isVisible ? 1 : 0, // Start completely transparent
          overflow: isVisible ? "visible" : "hidden", // Hide content until visible
          position: "relative",
          transition: "all 200ms ease-out", // Smooth transition when becoming visible
        }}
      />
      {!isVisible && (
        <span className="opacity-50 min-h-[1.2rem] inline align-start">
          {content || t("components.prompt_input.placeholder")}
        </span>
      )}
    </div>
  );
};
